import React, { useEffect, useState } from "react";
import {
  Row, Col, Card, Button, Input, Modal, Upload, Image,
  notification, Popconfirm, Spin, Tag, Typography,
} from "antd";
import {
  PlusOutlined, UploadOutlined, DeleteOutlined, EditOutlined, CheckOutlined, AppstoreOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config";
import PosterDesigner from "./components/PosterDesigner";
import RulesEngine from "./components/RulesEngine";
import ScheduleManager from "./components/ScheduleManager";
import WhatsappConfigManager from "./components/WhatsappConfigManager";
import PaymentReminderRules from "../sn_payment_reminder_rules";

const { Title, Text } = Typography;

// Build image URL from stored path
const buildImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) return imagePath;
  let clean = imagePath.replace(/^resources\//, "").replace(/^\//, "");
  if (!clean.startsWith("uploads/")) clean = "uploads/" + clean;
  const base = baseUrl.split("/api")[0];
  return `${base}/${clean}`;
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

function PosterList() {
  const history = useHistory();
  const [posters, setPosters]           = useState([]);
  const [loading, setLoading]           = useState(false);
  const [addModal, setAddModal]         = useState(false);
  const [newKey, setNewKey]             = useState("");
  const [newName, setNewName]           = useState("");
  const [creating, setCreating]         = useState(false);
  const [editingName, setEditingName]   = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);
  useEffect(() => { fetchPosters(); }, []);

  const fetchPosters = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/poster-config/list`, { headers: authHeaders() });
      if (res.data.status) setPosters(res.data.result || []);
    } catch (e) {
      notification.error({ message: "Failed to load poster list.", placement: "topRight" });
    } finally {
      setLoading(false);
    }
  };

  // ── Create new poster type ──────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!newKey.trim() || !newName.trim()) {
      notification.warning({ message: "Poster Key and Name are required.", placement: "topRight" });
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(
        `${baseUrl}/poster-config/create`,
        { poster_key: newKey.trim(), poster_name: newName.trim() },
        { headers: authHeaders() }
      );
      if (res.data.status) {
        notification.success({ message: res.data.message, placement: "topRight" });
        setAddModal(false);
        setNewKey(""); setNewName("");
        fetchPosters();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: "Error: " + e.message, placement: "topRight" });
    } finally {
      setCreating(false);
    }
  };

  // ── Rename poster ───────────────────────────────────────────────────────────
  const handleRename = async (poster_key) => {
    const newPosterName = editingName[poster_key];
    if (!newPosterName?.trim()) return;
    try {
      const res = await axios.patch(
        `${baseUrl}/poster-config/update/${poster_key}`,
        { poster_name: newPosterName.trim() },
        { headers: authHeaders() }
      );
      if (res.data.status) {
        notification.success({ message: "Name updated.", placement: "topRight" });
        setEditingName(prev => { const n = { ...prev }; delete n[poster_key]; return n; });
        fetchPosters();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    }
  };

  // ── Upload poster image ─────────────────────────────────────────────────────
  const handleImageUpload = async (file, poster_key) => {
    setUploadingKey(poster_key);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("poster_key", poster_key);
      const res = await axios.post(`${baseUrl}/poster-config/upload-image`, formData, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      if (res.data.status) {
        notification.success({ message: "Image uploaded successfully.", placement: "topRight" });
        fetchPosters();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: "Upload failed: " + e.message, placement: "topRight" });
    } finally {
      setUploadingKey(null);
    }
    return false; // prevent default upload
  };

  // ── Delete poster type ──────────────────────────────────────────────────────
  const handleDelete = async (poster_key) => {
    try {
      const res = await axios.delete(`${baseUrl}/poster-config/delete/${poster_key}`, {
        headers: authHeaders(),
      });
      if (res.data.status) {
        notification.success({ message: res.data.message, placement: "topRight" });
        fetchPosters();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    }
  };

  return (
    <div>
      {/* ── Schedule Manager ── */}
      <Row gutter={[24, 0]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <ScheduleManager />
        </Col>
      </Row>

      {/* ── WhatsApp Event Config ── */}
      <Row gutter={[24, 0]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <WhatsappConfigManager />
        </Col>
      </Row>

      {/* ── Poster Builder ── */}
      <Row gutter={[24, 0]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <Card
            bordered={false}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            style={{ backgroundColor: "#fff", borderRadius: 8 }}
            bodyStyle={{ padding: "16px" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <Title level={5} style={{ margin: 0 }}>Poster Builder</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Upload a poster, place text &amp; images, fill values and download.
                </Text>
              </div>
              <Button
                type="primary"
                icon={<AppstoreOutlined />}
                onClick={() => history.push("/poster-builder")}
              >
                Open Poster Builder
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── Rules Engine ── */}
      <Row gutter={[24, 0]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <RulesEngine posters={posters} />
        </Col>
      </Row>

      {/* ── Payment Reminder Rules ── */}
      <Row gutter={[24, 0]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <PaymentReminderRules />
        </Col>
      </Row>

      {/* ── Poster Library ── */}
      <Row gutter={[24, 0]}>
        <Col xs={24}>
          <Card
            bordered={false}
            title={
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Poster Settings</span>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setAddModal(true)}
                  size="small"
                >
                  Add New Poster
                </Button>
              </div>
            }
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            style={{ backgroundColor: "#fff", borderRadius: 8 }}
            bodyStyle={{ backgroundColor: "#fff", padding: "16px" }}
          >
            <Spin spinning={loading}>
              {posters.map((poster, index) => (
                <div key={poster.poster_key}>
                  {index > 0 && <hr style={{ borderColor: "#e8e8e8", margin: "24px 0" }} />}
                  <PosterCard
                    poster={poster}
                    editingName={editingName}
                    setEditingName={setEditingName}
                    uploadingKey={uploadingKey}
                    onRename={handleRename}
                    onImageUpload={handleImageUpload}
                    onDelete={handleDelete}
                    buildImageUrl={buildImageUrl}
                  />
                </div>
              ))}
              {!loading && posters.length === 0 && (
                <Text type="secondary">No posters found. Click "Add New Poster" to get started.</Text>
              )}
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Add New Poster Modal */}
      <Modal
        title="Add New Poster Type"
        open={addModal}
        onOk={handleCreate}
        onCancel={() => { setAddModal(false); setNewKey(""); setNewName(""); }}
        confirmLoading={creating}
        okText="Create"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong>Poster Key</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Unique slug — lowercase letters, numbers and _ only (e.g. birthday, annual_camp, purnima)
          </Text>
          <Input
            value={newKey}
            onChange={e => setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
            placeholder="e.g. birthday"
            style={{ marginTop: 4 }}
          />
        </div>
        <div>
          <Text strong>Poster Name</Text>
          <Input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="e.g. Birthday Poster"
            style={{ marginTop: 4 }}
          />
        </div>
      </Modal>
    </div>
  );
}

// ── Individual Poster Card ────────────────────────────────────────────────────
function PosterCard({ poster, editingName, setEditingName, uploadingKey, onRename, onImageUpload, onDelete, buildImageUrl }) {
  const imageUrl = buildImageUrl(poster.poster_image);
  const isEditingThisName = poster.poster_key in editingName;

  return (
    <div
      style={{
        border: "1px solid #d9d9d9",
        padding: 16,
        borderRadius: 8,
        backgroundColor: "#f9f9f9",
      }}
    >
      {/* Header row: name + key tag + delete */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        {isEditingThisName ? (
          <Input
            value={editingName[poster.poster_key]}
            onChange={e => setEditingName(prev => ({ ...prev, [poster.poster_key]: e.target.value }))}
            style={{ maxWidth: 260, fontWeight: 600 }}
            onPressEnter={() => onRename(poster.poster_key)}
          />
        ) : (
          <Title level={5} style={{ margin: 0 }}>{poster.poster_name}</Title>
        )}
        <Tag color="blue" style={{ fontFamily: "monospace" }}>{poster.poster_key}</Tag>

        {isEditingThisName ? (
          <Button
            size="small" type="primary" icon={<CheckOutlined />}
            onClick={() => onRename(poster.poster_key)}
          >
            Save
          </Button>
        ) : (
          <Button
            size="small" icon={<EditOutlined />}
            onClick={() => setEditingName(prev => ({ ...prev, [poster.poster_key]: poster.poster_name }))}
          >
            Rename
          </Button>
        )}

        <Popconfirm
          title="Delete this poster type?"
          onConfirm={() => onDelete(poster.poster_key)}
          okText="Yes"
          cancelText="No"
        >
          <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
        </Popconfirm>
      </div>

      {/* Image upload + preview row */}
      <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Upload
            beforeUpload={file => onImageUpload(file, poster.poster_key)}
            showUploadList={false}
            accept=".jpg,.jpeg"
          >
            <Button icon={<UploadOutlined />} loading={uploadingKey === poster.poster_key}>
              Upload Poster Image
            </Button>
          </Upload>
          <p style={{ color: "red", fontSize: 11, marginTop: 4, marginBottom: 0 }}>
            Size: <b>2400×3300</b> — <b>JPG</b>
          </p>
        </Col>
        {imageUrl && (
          <Col>
            <Image
              src={imageUrl}
              alt="Poster"
              width={50}
              height={50}
              style={{ borderRadius: 6, cursor: "pointer" }}
              preview={true}
            />
          </Col>
        )}
      </Row>

      {/* PosterDesigner */}
      <PosterDesigner
        posterImageUrl={imageUrl}
        posterType={poster.poster_key}
      />
    </div>
  );
}

export default PosterList;
